import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import { UntypedFormControl } from '@angular/forms';
import { translate } from '@jsverse/transloco';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

import { CustomFieldsService } from 'app/modules/field/custom-fields.service';
import { Template } from 'app/providers/dashboard-templates.service';
import { Field } from 'app/providers/storage-api/field.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';

import { WidgetDataSource } from '../../../../models';
import { Dashboard, Widget } from '../../../../providers/bridge-api/dashboard.service';
import {
  DashboardValidityChecks,
  ValidityResult,
} from '../../../dashboard/services/validity-checks';
import {
  DATASOURCE_GROUP_KEY,
  DatasourcesType,
} from '../../../dashboard/stores/datasource-manager.store';
import { AbstractChartReport } from '../../services/chart-report/chart-report-abstract/chart-report-abstract';

export interface AutocompleteState {
  timeout: any;
  inProgress: boolean;
  results: any[];
  filteredResults?: any[];
  control?: UntypedFormControl;
  observable?: Observable<any[]>;
}

export interface DataSourceItem {
  display: string;
  value: WidgetDataSource;
}

export class DatasourceManager {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  constructor(
    private dataSources: DatasourcesType,
    private placeholder: string
  ) {}

  public search(excludeItem?: WidgetDataSource, term: string = ''): DataSourceItem[] {
    let dataSourceItems: DataSourceItem[] = [];

    // first all groups
    if (this.dataSources.hasOwnProperty(DATASOURCE_GROUP_KEY)) {
      const dsList = this.dataSources[DATASOURCE_GROUP_KEY];
      dsList.forEach((ds) => {
        dataSourceItems.push({ display: this.displayForDataSource(ds), value: ds });
      });
    }

    // then everything else
    Object.entries(this.dataSources).forEach((entry) => {
      const collectionName = entry[0];
      if (collectionName !== DATASOURCE_GROUP_KEY) {
        const dsList = entry[1];
        dsList.forEach((ds) => {
          dataSourceItems.push({
            display: this.displayForDataSource(ds, dsList.length),
            value: ds,
          });
        });
      }
    });
    dataSourceItems.sort((a, b) => {
      return a.display?.localeCompare(b.display);
    });

    if (term) {
      dataSourceItems = dataSourceItems.filter((value) => {
        let valueToCheck = term;
        if (term.hasOwnProperty('display')) {
          valueToCheck = (term as unknown as DataSourceItem).display;
        }
        return value.display?.toLowerCase().indexOf(valueToCheck?.toLowerCase()) !== -1;
      });
    }
    if (excludeItem) {
      dataSourceItems = dataSourceItems.filter((value) => value.value.id !== excludeItem.id);
    }

    return dataSourceItems;
  }

  public displayForDataSource(ds: WidgetDataSource, analysisCount: number = 0): string {
    if (!ds) {
      return this.placeholder;
    }

    if (
      typeof ds.collectionDisplayName === 'undefined' ||
      typeof ds.collectionName === 'undefined'
    ) {
      return ds.displayName;
    } else if (ds.collectionDisplayName === ds.displayName || analysisCount === 1) {
      return ds.collectionDisplayName;
    }
    return `${ds.collectionDisplayName} (${ds.displayName})`;
  }
}

@EnableClassTelemetry()
@Component({
  selector: 'app-data-source-chooser',
  templateUrl: './data-source-chooser.component.html',
  styleUrls: ['./data-source-chooser.component.scss'],
  standalone: false,
})
export class DataSourceChooserComponent implements OnInit, OnChanges {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() value: WidgetDataSource;
  @Input() valuePlaceholder = translate('widgets.-none-');
  @Input() dataSources: DatasourcesType;
  @Input() disabled: boolean;
  @Input() widget?: Widget;
  @Input() report?: AbstractChartReport;
  @Input() dashboard?: Dashboard;
  @Input() accountMetadata?: Field[];
  @Input() compact?: boolean;
  @Input() showDatasourceChip = true;
  @Input() matInputClasses = [];
  @Input() overrideInternalChipLogic = false;
  @Input() template?: Template;

  @Output() datasourceSelected = new EventEmitter<WidgetDataSource>();
  @Output() datasourceCleared = new EventEmitter<void>();

  @ViewChild('elementDatasourceInput') elementDatasourceInputElement: ElementRef;

  public datasourceAc: AutocompleteState = {
    timeout: null,
    inProgress: false,
    results: [],
    control: new UntypedFormControl(),
  };

  public dataSourceItems: DataSourceItem[] = [];
  public dataSourceManager: DatasourceManager;
  selectedDatasource = '';
  datasourceProps: { [id: string]: ValidityResult } = {};

  constructor(private customFieldsService: CustomFieldsService) {}

  ngOnInit() {
    this.dataSourceManager = new DatasourceManager(this.dataSources, this.valuePlaceholder);
    this.dataSourceItems = this.dataSourceManager.search(
      this.overrideInternalChipLogic ? this.value : null
    );
    this.datasourceAc.observable = this.datasourceAc.control.valueChanges.pipe(
      startWith(''),
      map((term) =>
        this.dataSourceManager
          .search(this.overrideInternalChipLogic ? this.value : null, term)
          .filter((ds) => !this.datasourceProps[ds.value.id]?.hidden)
      )
    );
    this.fillDatasourceProps();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes.widget && changes.widget.previousValue !== this.widget) ||
      (changes.report && changes.report.previousValue !== this.report) ||
      (changes.dashboard && changes.dashboard.previousValue !== this.dashboard) ||
      (changes.template && !changes.template.firstChange)
    ) {
      this.fillDatasourceProps();
    }
  }

  public showDatasourceSearch() {
    if (!this.disabled) {
      this.showDatasourceChip = false;
      setTimeout(() => this.elementDatasourceInputElement.nativeElement.focus());
    }
  }

  public clearSelectedDatasource() {
    this.resetDataSource({});
    this.datasourceCleared.emit();
  }

  public get datasourceDisplayName(): string {
    return this.dataSourceManager.displayForDataSource(this.value);
  }

  public iconFor(ds: WidgetDataSource) {
    if (ds && ds.type === 'group') {
      return 'xi_folder';
    }
    return this.overrideInternalChipLogic ? 'document_chart' : 'xi_copy';
  }

  selectDatasource(item: DataSourceItem) {
    if (!item) {
      return;
    }

    this.resetDataSource({});

    if (this.value !== item.value) {
      this.value = item.value;
      this.datasourceSelected.emit(this.value);
    }
  }

  public updateDataSources() {
    clearTimeout(this.datasourceAc.timeout);
    this.datasourceAc.timeout = setTimeout(() => {
      this.datasourceAc.inProgress = true;
      this.datasourceAc.results = this.dataSourceManager.search(
        this.overrideInternalChipLogic ? this.value : null,
        this.selectedDatasource
      );
      this.datasourceAc.timeout = null;
      this.datasourceAc.inProgress = false;
    }, 400);
  }

  public isSelected(dataSource: WidgetDataSource) {
    return (
      this.value.analysisId === dataSource.analysisId &&
      this.value.groupId === dataSource.groupId &&
      this.value.collectionName === dataSource.collectionName
    );
  }

  public widgetRefreshed() {
    this.fillDatasourceProps();
  }

  public get dataSourceDocumentCount() {
    return this.value?.documentCount ?? 0;
  }

  private async fillDatasourceProps() {
    this.datasourceProps = {};

    if (this.widget) {
      this.dataSourceItems.forEach(async (item) => {
        const dsCustomFields = await this.customFieldsService.getMetadataSubTypesAsync(item.value);
        item.value.customFields = dsCustomFields;
        this.datasourceProps[item.value.id] = DashboardValidityChecks.dataSourceValidCheck(
          this.widget,
          this.report,
          item.value,
          this.accountMetadata,
          false,
          this.dashboard
        );
        this.fillDatasourceTooltip(item);
      });
      return;
    }

    if (this.dashboard) {
      this.dataSourceItems.forEach((item) => {
        this.datasourceProps[item.value.id] = DashboardValidityChecks.dataSourceValidCheckDashboard(
          this.dashboard,
          item.value,
          this.template
        );
        this.fillDatasourceTooltip(item);
      });
      return;
    }
  }

  private fillDatasourceTooltip(item: DataSourceItem) {
    if (!this.datasourceProps[item.value.id].tooltip && item.value.tags?.includes('fromWidget')) {
      this.datasourceProps[item.value.id].tooltip = this.dataSourceManager.displayForDataSource(
        item.value
      );
    }
  }

  resetDataSource($event) {
    // Prevent running when selecting a datasource or clicking on delete button
    if (
      !(
        $event.relatedTarget &&
        ($event.relatedTarget.nodeName === 'MAT-OPTION' ||
          $event.relatedTarget.classList.contains('data-source-chooser__clear-search'))
      )
    ) {
      if (!this.overrideInternalChipLogic) {
        this.showDatasourceChip = true;
      }
      setTimeout(() => {
        this.selectedDatasource = '';
      });
    }
  }
}
